import { supabase } from './supabase';
import { STATUS_COLORS, STATUS_DISPLAY } from './emailTemplates';
import { generateAnalyticsReportHTML, generateAnalyticsReportText } from './emailTemplates/analyticsReportTemplate';

class EmailService {
  // Send email via Edge Function
  async sendEmail(emailData) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session found');
      }

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: emailData,
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (error) {
        console.error('Email sending error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Email service error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send welcome email to new user
  async sendWelcomeEmail(user) {
    try {
      const templateData = {
        user_name: user.full_name || user.email.split('@')[0],
        dashboard_url: `${window.location.origin}/library`,
        header_title: 'Welcome to Solis Games!'
      };

      return await this.sendEmail({
        to: user.email,
        toName: user.full_name,
        subject: 'Welcome to Solis Games - Start Gaming Now!',
        templateType: 'welcome',
        templateData,
        userId: user.id,
        metadata: {
          trigger: 'user_registration',
          user_id: user.id
        }
      });
    } catch (error) {
      console.error('Welcome email error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send admin reply email for contact system
  async sendContactAdminReply(contact, adminMessage, adminUser) {
    try {
      // Check if user wants email notifications
      if (contact.notifications_enabled === false) {
        console.log('User has disabled email notifications for this ticket');
        return { success: true, data: { skipped: true, reason: 'Email notifications disabled' } };
      }

      let userProfile;

      // Handle anonymous contacts vs registered users
      if (!contact.user_id) {
        // Anonymous contact - use email from contact form
        if (!contact.email) {
          console.warn('Cannot send email: no user_id and no email provided');
          return { success: false, error: 'No recipient email available' };
        }

        // Use contact form email directly
        userProfile = {
          email: contact.email,
          full_name: contact.name || 'User'
        };
      } else {
        // Registered user - fetch from user_profiles
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('email, full_name')
          .eq('id', contact.user_id)
          .single();

        if (profileError || !profile) {
          console.error('Failed to fetch user profile:', profileError);
          return { success: false, error: 'User profile not found' };
        }

        userProfile = profile;
      }

      const templateData = {
        user_name: userProfile.full_name || userProfile.email.split('@')[0],
        admin_message: adminMessage,
        admin_name: adminUser.full_name || 'Support Team',
        ticket_id: contact.id,
        subject: contact.subject,
        status: this.getStatusDisplay(contact.status),
        ticket_url: `${window.location.origin}/user-profile-settings?tab=support&ticket=${contact.id}`,
        updated_at: new Date().toLocaleDateString(),
        header_title: 'Support Team Response'
      };

      return await this.sendEmail({
        to: userProfile.email,
        toName: userProfile.full_name,
        subject: `Response to your support ticket: ${contact.subject}`,
        templateType: 'ticket_response',
        templateData,
        userId: contact.user_id,
        contactId: contact.id,
        metadata: {
          trigger: 'admin_reply',
          contact_id: contact.id,
          admin_id: adminUser.id
        }
      });
    } catch (error) {
      console.error('Contact admin reply email error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send contact status update email
  async sendContactStatusUpdate(contact, adminNote = null) {
    try {
      let userProfile;

      // Handle anonymous contacts vs registered users
      if (!contact.user_id) {
        // Anonymous contact - use email from contact form
        if (!contact.email) {
          console.warn('Cannot send status email: no user_id and no email provided');
          return { success: false, error: 'No recipient email available' };
        }

        // Use contact form email directly
        userProfile = {
          email: contact.email,
          full_name: contact.name || 'User'
        };
      } else {
        // Registered user - fetch from user_profiles
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('email, full_name')
          .eq('id', contact.user_id)
          .single();

        if (profileError || !profile) {
          console.error('Failed to fetch user profile:', profileError);
          return { success: false, error: 'User profile not found' };
        }

        userProfile = profile;
      }

      const templateData = {
        user_name: userProfile.full_name || userProfile.email.split('@')[0],
        subject: contact.subject,
        status: contact.status,
        status_display: this.getStatusDisplay(contact.status),
        status_color: this.getStatusColor(contact.status),
        status_resolved: contact.status === 'resolved',
        ticket_id: contact.id,
        ticket_url: `${window.location.origin}/admin-contact-management?contact=${contact.id}`,
        admin_note: adminNote,
        updated_at: new Date().toLocaleDateString(),
        header_title: 'Ticket Update'
      };

      return await this.sendEmail({
        to: userProfile.email,
        toName: userProfile.full_name,
        subject: `Your support ticket has been ${contact.status} [Ticket #${contact.id}]`,
        templateType: 'contact_status_update',
        templateData,
        userId: contact.user_id,
        contactId: contact.id,
        metadata: {
          trigger: 'status_update',
          contact_id: contact.id,
          new_status: contact.status
        }
      });
    } catch (error) {
      console.error('Contact status update email error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send subscription confirmation email
  async sendSubscriptionConfirmation(user, subscription, plan) {
    try {
      const templateData = {
        user_name: user.full_name || user.email.split('@')[0],
        plan_name: plan.name,
        plan_price: plan.price,
        plan_features: plan.features,
        subscription_id: subscription.id,
        next_billing_date: new Date(subscription.current_period_end).toLocaleDateString(),
        dashboard_url: `${window.location.origin}/library`,
        manage_subscription_url: `${window.location.origin}/subscription-management`,
        header_title: 'Subscription Confirmed!'
      };

      return await this.sendEmail({
        to: user.email,
        toName: user.full_name,
        subject: 'Welcome to Solis Games Premium! 🎮',
        templateType: 'subscription_confirmation',
        templateData,
        userId: user.id,
        subscriptionId: subscription.id,
        metadata: {
          trigger: 'subscription_confirmed',
          plan_id: plan.id,
          subscription_id: subscription.id
        }
      });
    } catch (error) {
      console.error('Subscription confirmation email error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send subscription payment success email
  async sendSubscriptionPaymentSuccess(user, subscription, plan, paymentDetails) {
    try {
      const templateData = {
        user_name: user.full_name || user.email.split('@')[0],
        plan_name: plan.name,
        amount: paymentDetails.amount,
        payment_date: new Date(paymentDetails.created * 1000).toLocaleDateString(),
        next_billing_date: new Date(subscription.current_period_end).toLocaleDateString(),
        payment_method: paymentDetails.payment_method_details?.card ?
          `**** **** **** ${paymentDetails.payment_method_details.card.last4}` :
          'Payment method',
        dashboard_url: `${window.location.origin}/library`,
        billing_history_url: `${window.location.origin}/subscription-management`,
        header_title: 'Payment Confirmed'
      };

      return await this.sendEmail({
        to: user.email,
        toName: user.full_name,
        subject: 'Payment Confirmed - Solis Games Premium',
        templateType: 'subscription_payment_success',
        templateData,
        userId: user.id,
        subscriptionId: subscription.id,
        metadata: {
          trigger: 'payment_success',
          payment_intent_id: paymentDetails.id,
          amount: paymentDetails.amount
        }
      });
    } catch (error) {
      console.error('Subscription payment success email error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send subscription payment failed email
  async sendSubscriptionPaymentFailed(user, subscription, plan, failureDetails) {
    try {
      const templateData = {
        user_name: user.full_name || user.email.split('@')[0],
        plan_name: plan.name,
        amount: failureDetails.amount,
        payment_date: new Date().toLocaleDateString(),
        payment_method: failureDetails.payment_method ?
          `**** **** **** ${failureDetails.payment_method.card?.last4 || '****'}` :
          'Payment method',
        failure_reason: failureDetails.failure_reason || 'Payment could not be processed',
        retry_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(), // 3 days from now
        update_payment_url: `${window.location.origin}/subscription-management`,
        header_title: 'Payment Issue'
      };

      return await this.sendEmail({
        to: user.email,
        toName: user.full_name,
        subject: 'Action Required - Payment Issue with Your Solis Games Subscription',
        templateType: 'subscription_payment_failed',
        templateData,
        userId: user.id,
        subscriptionId: subscription.id,
        metadata: {
          trigger: 'payment_failed',
          failure_reason: failureDetails.failure_reason,
          amount: failureDetails.amount
        }
      });
    } catch (error) {
      console.error('Subscription payment failed email error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send subscription cancelled email
  async sendSubscriptionCancelled(user, subscription, plan) {
    try {
      const templateData = {
        user_name: user.full_name || user.email.split('@')[0],
        plan_name: plan.name,
        access_ends_date: new Date(subscription.current_period_end).toLocaleDateString(),
        reactivate_url: `${window.location.origin}/subscription-management`,
        feedback_url: `${window.location.origin}/contact?subject=Subscription Feedback`,
        header_title: 'Subscription Cancelled'
      };

      return await this.sendEmail({
        to: user.email,
        toName: user.full_name,
        subject: 'Your Solis Games subscription has been cancelled',
        templateType: 'subscription_cancelled',
        templateData,
        userId: user.id,
        subscriptionId: subscription.id,
        metadata: {
          trigger: 'subscription_cancelled',
          cancelled_at: new Date().toISOString(),
          access_ends: subscription.current_period_end
        }
      });
    } catch (error) {
      console.error('Subscription cancelled email error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send order confirmation email (called from client or manual trigger)
  async sendOrderConfirmation(user, order, items) {
    try {
      // Pre-format items as HTML for simple templates
      const itemsHtml = items.map(item => `
        <div style="border-bottom: 1px solid #eee; padding: 10px 0; display: flex; align-items: center;">
          <img src="${item.thumbnail_url}" alt="${item.name}" style="width: 50px; height: 50px; object-fit: cover; border-radius: 4px; margin-right: 15px;">
          <div>
            <div style="font-weight: bold;">${item.name}</div>
            <div style="color: #666; font-size: 14px;">Qty: ${item.quantity} · $${(item.retail_price / 100).toFixed(2)}</div>
          </div>
        </div>
      `).join('');

      const templateData = {
        user_name: user.full_name || user.email.split('@')[0],
        order_id: order.printful_order_id || order.id,
        short_id: order.id.slice(0, 8),
        total_amount: (order.total_amount / 100).toFixed(2),
        items_html: itemsHtml,
        shipping_name: order.shipping_address?.name,
        shipping_address1: order.shipping_address?.address1,
        shipping_city: order.shipping_address?.city,
        shipping_zip: order.shipping_address?.zip,
        dashboard_url: `${window.location.origin}/merch`,
        header_title: 'Order Confirmation'
      };

      return await this.sendEmail({
        to: user.email,
        toName: user.full_name,
        subject: `Order Confirmation #${order.printful_order_id || order.id.slice(0, 8)}`,
        templateType: 'order_confirmation', // Ensure this template exists in DB
        templateData,
        userId: user.id,
        metadata: {
          trigger: 'order_confirmation',
          order_id: order.id
        }
      });
    } catch (error) {
      console.error('Order confirmation email error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get email logs for a user (admin only)
  async getEmailLogs(filters = {}, limit = 50, offset = 0) {
    try {
      let query = supabase
        .from('email_logs')
        .select(`
          *,
          user_profiles!email_logs_user_id_fkey(id, full_name, email),
          contacts!email_logs_contact_id_fkey(id, subject)
        `);

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.templateType) {
        query = query.eq('template_type', filters.templateType);
      }
      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters.recipientEmail) {
        query = query.ilike('recipient_email', `%${filters.recipientEmail}%`);
      }
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching email logs:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getEmailLogs:', error);
      return { success: false, error: error.message };
    }
  }

  // Get email statistics (admin only)
  async getEmailStats(days = 30) {
    try {
      const { data, error } = await supabase.rpc('get_email_stats', { p_days: days });

      if (error) {
        console.error('Error fetching email stats:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data[0] || {} };
    } catch (error) {
      console.error('Error in getEmailStats:', error);
      return { success: false, error: error.message };
    }
  }

  // Get email templates (admin only)
  async getEmailTemplates() {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('template_type');

      if (error) {
        console.error('Error fetching email templates:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getEmailTemplates:', error);
      return { success: false, error: error.message };
    }
  }

  // Update email template (admin only)
  async updateEmailTemplate(templateId, updates) {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .update(updates)
        .eq('id', templateId)
        .select()
        .single();

      if (error) {
        console.error('Error updating email template:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateEmailTemplate:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper functions
  getStatusColor(status) {
    return STATUS_COLORS[status] || '#64748B';
  }

  getStatusDisplay(status) {
    return STATUS_DISPLAY[status] || status;
  }

  // Format email template type for display
  formatTemplateType(templateType) {
    const labels = {
      'welcome': 'Welcome Email',
      'subscription_confirmation': 'Subscription Confirmation',
      'subscription_payment_success': 'Payment Success',
      'subscription_payment_failed': 'Payment Failed',
      'subscription_cancelled': 'Subscription Cancelled',
      'subscription_trial_ending': 'Trial Ending',
      'contact_admin_reply': 'Support Reply',
      'contact_status_update': 'Status Update',
      'contact_new_ticket': 'New Ticket'
    };
    return labels[templateType] || templateType;
  }

  // Format email status for display
  formatEmailStatus(status) {
    const labels = {
      'pending': 'Pending',
      'sent': 'Sent',
      'delivered': 'Delivered',
      'bounced': 'Bounced',
      'failed': 'Failed',
      'opened': 'Opened',
      'clicked': 'Clicked'
    };
    return labels[status] || status;
  }

  // Get status color for email status
  getEmailStatusColor(status) {
    const colors = {
      'pending': '#D97706',
      'sent': '#6366F1',
      'delivered': '#059669',
      'bounced': '#DC2626',
      'failed': '#DC2626',
      'opened': '#10B981',
      'clicked': '#8B5CF6'
    };
    return colors[status] || '#64748B';
  }

  // Subscribe to email log updates (admin only)
  subscribeToEmailLogs(callback) {
    const subscription = supabase
      .channel('email_logs')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'email_logs'
        },
        (payload) => {
          console.log('Email log update:', payload);
          callback(payload);
        }
      )
      .subscribe();

    return subscription;
  }

  // Unsubscribe from email log updates
  unsubscribeFromEmailLogs(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }

  /**
   * ============================================
   * ANALYTICS REPORTS
   * ============================================
   */

  /**
   * Send weekly analytics report to studio owner
   * @param {Object} studio - Studio object with owner information
   * @param {Object} analyticsData - Analytics data for the report
   * @returns {Promise<Object>} Result of email send operation
   */
  async sendWeeklyAnalyticsReport(studio, analyticsData) {
    try {
      // Get studio owner profile
      const { data: ownerProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('email, full_name, analytics_emails_enabled')
        .eq('id', studio.owner_id)
        .single();

      if (profileError || !ownerProfile) {
        console.error('Owner profile not found for analytics report:', studio.owner_id);
        return { success: false, error: 'Owner profile not found' };
      }

      // Check if user has analytics emails enabled
      if (ownerProfile.analytics_emails_enabled === false) {
        console.log('User has disabled analytics emails:', ownerProfile.email);
        return { success: true, data: { skipped: true, reason: 'Analytics emails disabled' } };
      }

      // Prepare template data
      const templateData = {
        studio_name: studio.name,
        user_name: ownerProfile.full_name || ownerProfile.email.split('@')[0],
        report_period: analyticsData.report_period || 'Past 7 Days',
        games_summary: analyticsData.games_summary || [],
        top_game: analyticsData.top_game || null,
        total_players: analyticsData.total_players || 0,
        total_sessions: analyticsData.total_sessions || 0,
        avg_session_duration: analyticsData.avg_session_duration || 0,
        retention_summary: analyticsData.retention_summary || { day7: 0 },
        peak_hours: analyticsData.peak_hours || null,
        insights: analyticsData.insights || [],
        dashboard_url: `${window.location.origin}/studio/${studio.id}/analytics`
      };

      // Generate HTML and text versions
      const htmlContent = generateAnalyticsReportHTML(templateData);
      const textContent = generateAnalyticsReportText(templateData);

      // Send email using custom HTML template
      return await this.sendEmail({
        to: ownerProfile.email,
        toName: ownerProfile.full_name,
        subject: `📊 Weekly Analytics Report - ${studio.name}`,
        templateType: 'analytics_weekly_report',
        htmlContent: htmlContent,
        textContent: textContent,
        templateData,
        userId: studio.owner_id,
        metadata: {
          trigger: 'weekly_analytics_report',
          studio_id: studio.id,
          report_period: templateData.report_period
        }
      });
    } catch (error) {
      console.error('Error sending weekly analytics report:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send milestone achievement notification
   * @param {Object} studio - Studio object
   * @param {Object} milestone - Milestone achievement data
   * @returns {Promise<Object>} Result of email send operation
   */
  async sendMilestoneNotification(studio, milestone) {
    try {
      // Get studio owner profile
      const { data: ownerProfile } = await supabase
        .from('user_profiles')
        .select('email, full_name, milestone_emails_enabled')
        .eq('id', studio.owner_id)
        .single();

      if (!ownerProfile || ownerProfile.milestone_emails_enabled === false) {
        return { success: true, data: { skipped: true } };
      }

      const templateData = {
        studio_name: studio.name,
        user_name: ownerProfile.full_name || ownerProfile.email.split('@')[0],
        milestone_type: milestone.type,
        milestone_value: milestone.value,
        milestone_title: milestone.title,
        milestone_message: milestone.message,
        game_title: milestone.game_title || null,
        dashboard_url: `${window.location.origin}/studio/${studio.id}/analytics`
      };

      return await this.sendEmail({
        to: ownerProfile.email,
        toName: ownerProfile.full_name,
        subject: `🎉 Milestone Achieved - ${milestone.title}`,
        templateType: 'milestone_notification',
        templateData,
        userId: studio.owner_id,
        metadata: {
          trigger: 'milestone_achieved',
          studio_id: studio.id,
          milestone_type: milestone.type
        }
      });
    } catch (error) {
      console.error('Error sending milestone notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get analytics report preferences for a studio
   * @param {string} studioId - Studio UUID
   * @returns {Promise<Object>} Studio analytics preferences
   */
  async getAnalyticsReportPreferences(studioId) {
    try {
      const { data, error } = await supabase
        .from('studio_analytics_preferences')
        .select('*')
        .eq('studio_id', studioId)
        .single();

      if (error && error.code !== 'PGRST116') { // Not found is okay
        throw error;
      }

      return {
        success: true,
        data: data || {
          weekly_reports_enabled: true,
          milestone_notifications_enabled: true,
          report_day: 'monday',
          report_time: '09:00'
        }
      };
    } catch (error) {
      console.error('Error fetching analytics preferences:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update analytics report preferences for a studio
   * @param {string} studioId - Studio UUID
   * @param {Object} preferences - Updated preferences
   * @returns {Promise<Object>} Update result
   */
  async updateAnalyticsReportPreferences(studioId, preferences) {
    try {
      const { data, error } = await supabase
        .from('studio_analytics_preferences')
        .upsert({
          studio_id: studioId,
          ...preferences,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'studio_id'
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error updating analytics preferences:', error);
      return { success: false, error: error.message };
    }
  }
}

const emailService = new EmailService();
export default emailService;
